/**
 * VisitTimeline Component
 * 
 * Displays a chronological timeline of all events related to a visit/appointment.
 * Includes SOAP notes, prescriptions, lab orders, messages, and more.
 */

import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  FileText, 
  Pill, 
  FlaskConical, 
  MessageCircle, 
  Video,
  CheckCircle2,
  PlayCircle,
  Users,
  Stethoscope,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import apiClient from '@/services/api';

// Event types from backend
const EVENT_TYPES = {
  APPOINTMENT_START: 'appointment_start',
  CHECK_IN: 'check_in',
  TELEHEALTH_JOIN: 'telehealth_join',
  ENCOUNTER_START: 'encounter_start',
  NOTE_CREATED: 'note_created',
  PRESCRIPTION: 'prescription',
  LAB_ORDER: 'lab_order',
  MESSAGE: 'message',
  FOLLOW_UP: 'follow_up',
  APPOINTMENT_END: 'appointment_end',
};

interface TimelineEvent {
  id: string;
  type: string;
  timestamp: string;
  title: string;
  description?: string;
  expandable?: boolean;
  data?: Record<string, any>;
}

interface TimelineData {
  appointment: {
    id: string;
    start_time: string;
    end_time: string;
    status: string;
    visit_mode: string;
    appointment_type?: string;
  };
  patient: {
    id: string;
    first_name: string;
    last_name: string;
    dob?: string;
  };
  practitioner: {
    id: string;
    first_name: string;
    last_name: string;
    credentials?: string;
  };
  clinic?: {
    id: string;
    name: string;
  };
  events: TimelineEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalNotes: number;
    totalPrescriptions: number;
    totalLabOrders: number;
  };
  transcript?: any;
  messages?: any;
}

interface VisitTimelineProps {
  appointmentId: string;
  clinicId: string;
  compact?: boolean;
  limit?: number;
  showHeader?: boolean;
  onEventClick?: (event: TimelineEvent) => void;
}

const getEventIcon = (type: string) => {
  switch (type) {
    case EVENT_TYPES.APPOINTMENT_START:
      return <PlayCircle className="w-4 h-4" />;
    case EVENT_TYPES.CHECK_IN:
      return <CheckCircle2 className="w-4 h-4" />;
    case EVENT_TYPES.TELEHEALTH_JOIN:
      return <Video className="w-4 h-4" />;
    case EVENT_TYPES.ENCOUNTER_START:
      return <Stethoscope className="w-4 h-4" />;
    case EVENT_TYPES.NOTE_CREATED:
      return <FileText className="w-4 h-4" />;
    case EVENT_TYPES.PRESCRIPTION:
      return <Pill className="w-4 h-4" />;
    case EVENT_TYPES.LAB_ORDER:
      return <FlaskConical className="w-4 h-4" />;
    case EVENT_TYPES.MESSAGE:
      return <MessageCircle className="w-4 h-4" />;
    case EVENT_TYPES.FOLLOW_UP:
      return <Calendar className="w-4 h-4" />;
    case EVENT_TYPES.APPOINTMENT_END:
      return <CheckCircle2 className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

const getEventColor = (type: string) => {
  switch (type) {
    case EVENT_TYPES.APPOINTMENT_START:
    case EVENT_TYPES.APPOINTMENT_END:
      return 'bg-blue-500';
    case EVENT_TYPES.CHECK_IN:
      return 'bg-green-500';
    case EVENT_TYPES.TELEHEALTH_JOIN:
      return 'bg-purple-500';
    case EVENT_TYPES.ENCOUNTER_START:
      return 'bg-teal-500';
    case EVENT_TYPES.NOTE_CREATED:
      return 'bg-amber-500';
    case EVENT_TYPES.PRESCRIPTION:
      return 'bg-pink-500';
    case EVENT_TYPES.LAB_ORDER:
      return 'bg-cyan-500';
    case EVENT_TYPES.MESSAGE:
      return 'bg-indigo-500';
    case EVENT_TYPES.FOLLOW_UP:
      return 'bg-orange-500';
    default:
      return 'bg-gray-500';
  }
};

const VisitTimeline: React.FC<VisitTimelineProps> = ({
  appointmentId,
  clinicId,
  compact = false,
  limit = 50,
  showHeader = true,
  onEventClick,
}) => {
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  useEffect(() => {
    loadTimeline();
  }, [appointmentId, page]);

  const loadTimeline = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get(
        `/emr/${clinicId}/appointments/${appointmentId}/timeline`,
        { params: { page, limit } }
      );

      if (response.data.success) {
        setTimelineData(response.data.data);
      } else {
        setError(response.data.error?.message || 'Failed to load timeline');
      }
    } catch (err: any) {
      console.error('Error loading timeline:', err);
      setError(err.response?.data?.error?.message || 'Failed to load timeline');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTranscript = async () => {
    if (!timelineData || timelineData.transcript) return;
    try {
      setIsLoadingTranscript(true);
      const response = await apiClient.get(
        `/emr/${clinicId}/appointments/${appointmentId}/timeline`,
        { params: { includeTranscript: 'true' } }
      );
      if (response.data.success) {
        setTimelineData(prev => ({
          ...prev!,
          transcript: response.data.data.transcript,
        }));
      }
    } catch (err) {
      console.error('Error loading transcript:', err);
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  const loadMessages = async () => {
    if (!timelineData || timelineData.messages) return;
    try {
      setIsLoadingMessages(true);
      const response = await apiClient.get(
        `/emr/${clinicId}/appointments/${appointmentId}/timeline`,
        { params: { includeMessages: 'true' } }
      );
      if (response.data.success) {
        setTimelineData(prev => ({
          ...prev!,
          messages: response.data.data.messages,
        }));
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const toggleEventExpanded = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const handleEventClick = (event: TimelineEvent) => {
    if (onEventClick) {
      onEventClick(event);
    } else if (event.expandable) {
      toggleEventExpanded(event.id);
    }
  };

  if (isLoading && !timelineData) {
    return (
      <div className="space-y-4">
        {showHeader && <Skeleton className="h-20 w-full" />}
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex gap-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="w-12 h-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" size="sm" onClick={loadTimeline}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!timelineData) {
    return null;
  }

  const { appointment, patient, practitioner, events, pagination, summary } = timelineData;

  return (
    <div className={cn('space-y-4', compact && 'space-y-2')}>
      {/* Header */}
      {showHeader && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {patient.first_name} {patient.last_name}
                </CardTitle>
                <CardDescription>
                  {appointment.appointment_type || 'Appointment'} with {practitioner.first_name} {practitioner.last_name}
                  {practitioner.credentials && `, ${practitioner.credentials}`}
                </CardDescription>
              </div>
              <Badge variant={appointment.status === 'completed' ? 'default' : 'secondary'}>
                {appointment.visit_mode === 'telehealth' && <Video className="w-3 h-3 mr-1" />}
                {appointment.status}
              </Badge>
            </div>
            <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {format(parseISO(appointment.start_time), 'MMM d, yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {format(parseISO(appointment.start_time), 'h:mm a')}
              </span>
            </div>
          </CardHeader>
          {/* Summary badges */}
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {summary.totalNotes > 0 && (
                <Badge variant="outline" className="text-xs">
                  <FileText className="w-3 h-3 mr-1" />
                  {summary.totalNotes} Note{summary.totalNotes > 1 ? 's' : ''}
                </Badge>
              )}
              {summary.totalPrescriptions > 0 && (
                <Badge variant="outline" className="text-xs">
                  <Pill className="w-3 h-3 mr-1" />
                  {summary.totalPrescriptions} Rx
                </Badge>
              )}
              {summary.totalLabOrders > 0 && (
                <Badge variant="outline" className="text-xs">
                  <FlaskConical className="w-3 h-3 mr-1" />
                  {summary.totalLabOrders} Lab{summary.totalLabOrders > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

        <div className="space-y-1">
          {events.map((event, index) => (
            <div
              key={event.id}
              className={cn(
                'relative flex items-start gap-3 pl-0 pr-2 py-2 rounded-lg transition-colors',
                event.expandable && 'cursor-pointer hover:bg-muted/50',
                !compact && 'py-3'
              )}
              onClick={() => handleEventClick(event)}
            >
              {/* Icon circle */}
              <div
                className={cn(
                  'relative z-10 flex items-center justify-center w-8 h-8 rounded-full text-white shrink-0',
                  getEventColor(event.type)
                )}
              >
                {getEventIcon(event.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={cn('font-medium', compact && 'text-sm')}>
                    {event.title}
                  </p>
                  {event.expandable && (
                    expandedEvents.has(event.id) ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )
                  )}
                </div>
                {event.description && (
                  <p className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
                    {event.description}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {format(parseISO(event.timestamp), 'h:mm a')}
                  {!compact && (
                    <span className="ml-2">
                      ({formatDistanceToNow(parseISO(event.timestamp), { addSuffix: true })})
                    </span>
                  )}
                </p>

                {/* Expanded content */}
                {event.expandable && expandedEvents.has(event.id) && (
                  <Collapsible open={true}>
                    <CollapsibleContent className="mt-2 p-3 bg-muted/30 rounded-md">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(event.data, null, 2)}
                      </pre>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lazy load buttons */}
      {appointment.visit_mode === 'telehealth' && !timelineData.transcript && (
        <Button
          variant="outline"
          size="sm"
          onClick={loadTranscript}
          disabled={isLoadingTranscript}
          className="w-full"
        >
          {isLoadingTranscript ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading Transcript...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Load Transcript
            </>
          )}
        </Button>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Showing {events.length} of {pagination.total} events
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Transcript display */}
      {timelineData.transcript && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Telehealth Transcript</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-60 overflow-y-auto text-sm bg-muted/30 rounded p-3">
              <pre className="whitespace-pre-wrap">{timelineData.transcript.content}</pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VisitTimeline;
export type { TimelineData, TimelineEvent };








